import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import DualActionFooter from '@/components/feature/DualActionFooter';
import ClientLogosStrip from '@/components/feature/ClientLogosStrip';
import HowWeVetEngineers from '@/components/feature/HowWeVetEngineers';
import SoftwareDevFAQ from './components/SoftwareDevFAQ';
import { services } from '@/mocks/services';
import DevPricingSection from './components/DevPricingSection';
import RecentProjectsCarousel from './components/RecentProjectsCarousel';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const devCategories = [
  { id: 'strategy-design', label: 'Strategy & Design', color: 'rose' },
  { id: 'development', label: 'Development', color: 'sky' },
  { id: 'cloud-ai', label: 'Cloud & AI', color: 'violet' },
  { id: 'quality-support', label: 'Quality & Support', color: 'emerald' },
];

const colorMap: Record<string, { bg: string; border: string; iconBg: string; text: string; lightBg: string }> = {
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', iconBg: 'bg-rose-500', text: 'text-rose-600', lightBg: 'bg-rose-100' },
  sky: { bg: 'bg-sky-50', border: 'border-sky-200', iconBg: 'bg-sky-500', text: 'text-sky-600', lightBg: 'bg-sky-100' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', iconBg: 'bg-violet-500', text: 'text-violet-600', lightBg: 'bg-violet-100' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', iconBg: 'bg-emerald-500', text: 'text-emerald-600', lightBg: 'bg-emerald-100' },
};

// ── Lead Capture Form ──
function LeadCaptureForm() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      data.append(key, value as string);
    }
    fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => {
        setModalType('success');
        setShowModal(true);
        form.reset();
        // Google Analytics conversion event
        if (typeof window !== 'undefined' && (window as Record<string, unknown>).gtag) {
          (window as Record<string, unknown>).gtag('event', 'generate_lead', {
            event_category: 'software_development',
            event_label: 'project_roadmap_request',
            value: 1,
          });
        }
      })
      .catch(() => {
        setModalType('error');
        setShowModal(true);
      });
  };

  return (
    <section id="cta" className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      {/* Thank You Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 20 }}
              transition={{ duration: 0.4, type: 'spring', bounce: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl md:rounded-3xl p-8 md:p-12 max-w-md w-full shadow-2xl text-center"
            >
              {modalType === 'success' ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15, ...springTransition }}
                    className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-primary-500 rounded-full mx-auto mb-6 shadow-lg"
                  >
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-check-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </motion.div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">Thank You!</h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Your project inquiry has been submitted. Our engineering team will review your requirements and send a proposed roadmap within 24 hours.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center bg-red-500 rounded-full mx-auto mb-6 shadow-lg">
                    <motion.i
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                      className="ri-error-warning-line text-4xl md:text-5xl text-white"
                    ></motion.i>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-3">Oops!</h3>
                  <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                    Something went wrong. Please try again or email us directly at{' '}
                    <a href="mailto:info@thoughtic.com" className="text-primary-600 hover:underline">info@thoughtic.com</a>.
                  </p>
                </>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="px-8 py-3 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                {modalType === 'success' ? 'Got It' : 'Try Again'}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6">
              <i className="ri-rocket-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-white/80">Start Your Project</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
              Get a Free
              <span className="text-primary-400"> Project Roadmap</span>
            </h2>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6 md:mb-8">
              Tell us what you are building and our senior engineers will respond with a proposed architecture, team structure, timeline, and fixed or T&M estimate — within 24 hours.
            </p>
            <div className="space-y-3 md:space-y-4">
              {[
                'No-obligation technical consultation',
                'Detailed scope and architecture review',
                'Team composition and timeline estimate',
                'Transparent pricing model',
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ ...springTransition, duration: 0.5, delay: 0.35 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-xs text-white"></i>
                  </div>
                  <span className="text-sm text-gray-300">{feature}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Form */}
          <motion.div
            initial={{ opacity: 0, x: 30, scale: 0.95 }}
            animate={inView ? { opacity: 1, x: 0, scale: 1 } : {}}
            transition={{ ...springTransition, duration: 0.8, delay: 0.3 }}
            className="bg-white rounded-xl md:rounded-2xl p-6 md:p-8 shadow-xl"
          >
            <div className="text-center mb-4 md:mb-6">
              <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-2">Request a Roadmap</h3>
              <p className="text-xs md:text-sm text-gray-600">Share your vision and we will build the plan</p>
            </div>

            <form
              className="space-y-3 md:space-y-4"
              method="POST"
              action="https://readdy.ai/api/form/d89b7c04og5fkb1kt6qg"
              encType="application/x-www-form-urlencoded"
              data-readdy-form
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label htmlFor="sd-name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="sd-name"
                    name="name"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="sd-email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Work Email
                  </label>
                  <input
                    type="email"
                    id="sd-email"
                    name="email"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="sd-company" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Company Name
                </label>
                <input
                  type="text"
                  id="sd-company"
                  name="company"
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                  placeholder="Acme Inc."
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label htmlFor="sd-service" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Service Interest
                  </label>
                  <select
                    id="sd-service"
                    name="service_interest"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                    required
                  >
                    <option value="">Select a service</option>
                    <optgroup label="Strategy & Design">
                      <option value="Product Discovery & Strategy">Product Discovery & Strategy</option>
                      <option value="UI/UX Design Services">UI/UX Design Services</option>
                    </optgroup>
                    <optgroup label="Development">
                      <option value="Custom Software Development">Custom Software Development</option>
                      <option value="SaaS Product Development">SaaS Product Development</option>
                      <option value="Web Application Development">Web Application Development</option>
                      <option value="Mobile App Development">Mobile App Development</option>
                      <option value="API & System Integrations">API & System Integrations</option>
                    </optgroup>
                    <optgroup label="Cloud & AI">
                      <option value="Cloud & DevOps Engineering">Cloud & DevOps Engineering</option>
                      <option value="AI & Automation Solutions">AI & Automation Solutions</option>
                    </optgroup>
                    <optgroup label="Quality & Support">
                      <option value="QA & Testing Services">QA & Testing Services</option>
                      <option value="Product Maintenance & Support">Product Maintenance & Support</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label htmlFor="sd-budget" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Budget Range
                  </label>
                  <select
                    id="sd-budget"
                    name="budget_range"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select budget</option>
                    <option value="$10k - $50k">$10k – $50k</option>
                    <option value="$50k - $150k">$50k – $150k</option>
                    <option value="$150k - $500k">$150k – $500k</option>
                    <option value="$500k+">$500k+</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <div>
                  <label htmlFor="sd-timeline" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    Desired Timeline
                  </label>
                  <select
                    id="sd-timeline"
                    name="timeline"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select timeline</option>
                    <option value="Less than 3 months">Less than 3 months</option>
                    <option value="3 - 6 months">3 – 6 months</option>
                    <option value="6 - 12 months">6 – 12 months</option>
                    <option value="12+ months">12+ months</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="sd-source" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                    How Did You Find Us?
                  </label>
                  <select
                    id="sd-source"
                    name="source"
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select source</option>
                    <option value="Google Search">Google Search</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Referral">Referral</option>
                    <option value="Clutch">Clutch</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="sd-description" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">
                  Project Description <span className="text-gray-400 font-normal">(max 500 chars)</span>
                </label>
                <textarea
                  id="sd-description"
                  name="project_description"
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm resize-none"
                  placeholder="Describe your project goals, target users, and any technical requirements..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full py-3 md:py-4 bg-primary-500 text-white text-sm md:text-base font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
              >
                Request Project Roadmap
                <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
              </button>

              <p className="text-xs text-center text-gray-500 mt-3">
                By submitting, you agree to our{' '}
                <Link to="/terms" className="text-primary-600 hover:underline cursor-pointer">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-primary-600 hover:underline cursor-pointer">Privacy Policy</Link>
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ── Hero ── */
function Hero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-24 md:pt-32 pb-16 md:pb-0 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20technology%20background%20with%20flowing%20lines%20of%20code%2C%20geometric%20circuit%20patterns%2C%20and%20soft%20amber%20and%20teal%20glow%20effects%2C%20deep%20navy%20blue%20base%2C%20futuristic%20software%20development%20aesthetic%2C%20clean%20minimal%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20premium%20corporate%20feel&width=1600&height=900&seq=softdev-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-3xl"></div>
      </div>

      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-4xl mx-auto  pb-5"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-code-box-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Software Development</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Build Software That
            <br />
            <span className="text-primary-400">Scales & Delivers</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            From product strategy to deployed code — we design, engineer, and launch custom software for startups and enterprises. End-to-end delivery with full transparency.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a
              href="#cta"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Get a Project Roadmap
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </a>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              See How We Build
            </Link>
          </motion.div>
        </motion.div>
        <ClientLogosStrip />
      </div>
    </div>
  );
}

/* ── Stats Bar ── */
function StatsBar() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const stats = [
    { value: '150+', label: 'Apps Delivered', icon: 'ri-apps-line' },
    { value: '40%', label: 'Faster to Market', icon: 'ri-timer-flash-line' },
    { value: '99.9%', label: 'Uptime SLA', icon: 'ri-shield-check-line' },
    { value: '11', label: 'Service Verticals', icon: 'ri-stack-line' },
  ];

  return (
    <section className="py-10 md:py-14 bg-white border-b border-gray-100" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08, ...springTransition }}
              className="text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 mx-auto mb-2">
                <i className={`${stat.icon} text-primary-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Why Build With Us ── */
function WhyBuildWithUs() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const reasons = [
    {
      icon: 'ri-compass-discover-line',
      title: 'Strategy-Led Delivery',
      desc: 'Every project starts with discovery, user research, and a validated roadmap — so you build the right product, not just any product.',
      stat: '40%',
      statLabel: 'faster time-to-market',
    },
    {
      icon: 'ri-code-s-slash-line',
      title: 'Modern Tech Stacks',
      desc: 'We engineer with React, Next.js, Node.js, Python, AWS, Kubernetes, and AI frameworks — always choosing the right tool for your outcome.',
      stat: '50+',
      statLabel: 'technologies mastered',
    },
    {
      icon: 'ri-loop-left-line',
      title: 'Agile & Transparent',
      desc: 'Bi-weekly sprints, live dashboards, and direct access to your engineers. No black boxes — you see progress every day.',
      stat: '2wk',
      statLabel: 'sprint cycles',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality & Compliance',
      desc: 'ISO 27001-aligned processes, automated testing pipelines, and security reviews baked into every sprint.',
      stat: '99.9%',
      statLabel: 'uptime SLA',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-award-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Thoughtic Engineering</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Engineering Excellence,
            <span className="text-primary-600"> Start to Finish</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We do not just write code. We architect products, design experiences, and operate infrastructure — with the rigor your business demands.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {reasons.map((r, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${r.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3">
                <span className="text-2xl md:text-3xl font-bold text-primary-600">{r.stat}</span>
                <p className="text-xs text-gray-500 mt-0.5">{r.statLabel}</p>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{r.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Services Grid ── */
function ServicesGrid() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const devServices = services.filter((s) =>
    ['strategy-design', 'development', 'cloud-ai', 'quality-support'].includes(s.category)
  );

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-stack-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">What We Build</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            End-to-End
            <span className="text-primary-600"> Software Services</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Every capability you need to take a product from idea to market — and keep it growing.
          </motion.p>
        </motion.div>

        {devCategories.map((cat) => {
          const catServices = devServices.filter((s) => s.category === cat.id);
          if (catServices.length === 0) return null;
          const c = colorMap[cat.color];

          return (
            <div key={cat.id} className="mb-12 md:mb-16 last:mb-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3, ...springTransition }}
                className="flex items-center gap-3 mb-6 md:mb-8"
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${c.iconBg}`}>
                  <i className="ri-folder-line text-white text-sm"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-navy-900">{cat.label}</h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {catServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 25, scale: 0.97 }}
                    animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ delay: 0.35 + index * 0.06, ...springTransition, duration: 0.5 }}
                    className="group bg-white rounded-2xl border border-gray-100 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-500/5 transition-all duration-500 overflow-hidden"
                  >
                    <div className="p-5 md:p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg ${c.iconBg}`}>
                          <i className={`${service.icon} text-white text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base md:text-lg font-bold text-navy-900 leading-snug">
                            {service.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm font-bold text-primary-600">{service.stat}</span>
                            <span className="text-xs text-gray-400">{service.statLabel}</span>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        {service.fullDescription}
                      </p>

                      <div className="space-y-2 mb-5">
                        {service.features.slice(0, 4).map((feature) => (
                          <div key={feature} className="flex items-start gap-2">
                            <div className="w-4 h-4 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-[10px] text-primary-600"></i>
                            </div>
                            <span className="text-xs text-gray-600">{feature}</span>
                          </div>
                        ))}
                        {service.features.length > 4 && (
                          <div className="text-xs text-gray-400 pl-6">+{service.features.length - 4} more capabilities</div>
                        )}
                      </div>

                      <a
                        href="#cta"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer group/link"
                      >
                        Discuss your needs
                        <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform duration-300"></i>
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── Dev Process ── */
function DevProcess() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const steps = [
    {
      num: '01',
      title: 'Discovery & Strategy',
      desc: 'We validate your idea, research the market, define the MVP scope, and create a technical architecture plan.',
      icon: 'ri-compass-discover-line',
    },
    {
      num: '02',
      title: 'Design & Prototyping',
      desc: 'User research, wireframes, design systems, and interactive prototypes — built for usability and brand consistency.',
      icon: 'ri-palette-line',
    },
    {
      num: '03',
      title: 'Engineering & Sprints',
      desc: 'Agile two-week sprints with CI/CD, code reviews, and daily standups. Transparent progress you can track.',
      icon: 'ri-code-s-slash-line',
    },
    {
      num: '04',
      title: 'QA & Testing',
      desc: 'Automated and manual testing at every layer — unit, integration, performance, and security — before any release.',
      icon: 'ri-bug-line',
    },
    {
      num: '05',
      title: 'Deployment & DevOps',
      desc: 'Cloud-native deployment on AWS, Azure, or GCP with monitoring, auto-scaling, and zero-downtime releases.',
      icon: 'ri-rocket-line',
    },
    {
      num: '06',
      title: 'Support & Growth',
      desc: 'Ongoing maintenance, feature enhancements, performance tuning, and 24/7 monitoring to keep your product competitive.',
      icon: 'ri-tools-line',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-route-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Our Process</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            How We Build
            <span className="text-primary-600"> Software</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            A battle-tested product development lifecycle designed to reduce risk, maintain quality, and ship on time.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="text-3xl font-black text-primary-200 mb-3">{step.num}</div>
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${step.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Industries We Build For ── */
function IndustriesSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const industries = [
    { name: 'Fintech', icon: 'ri-bank-line', desc: 'Secure payment platforms, trading systems, and banking APIs with regulatory compliance.' },
    { name: 'Healthcare', icon: 'ri-heart-pulse-line', desc: 'HIPAA-compliant telehealth apps, patient portals, and clinical data systems.' },
    { name: 'E-Commerce', icon: 'ri-shopping-cart-line', desc: 'High-conversion storefronts, inventory management, and multi-vendor marketplaces.' },
    { name: 'SaaS', icon: 'ri-cloud-line', desc: 'Multi-tenant platforms with subscription billing, analytics, and admin dashboards.' },
    { name: 'EdTech', icon: 'ri-graduation-cap-line', desc: 'Learning management systems, course platforms, and student engagement tools.' },
    { name: 'Logistics', icon: 'ri-truck-line', desc: 'Fleet tracking, route optimization, and supply chain visibility platforms.' },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-building-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Industry Expertise</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Industries We
            <span className="text-primary-600"> Build For</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Deep domain knowledge across sectors that demand security, scale, and regulatory rigor.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {industries.map((ind, index) => (
            <motion.div
              key={ind.name}
              initial={{ opacity: 0, y: 25, scale: 0.96 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${ind.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{ind.name}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{ind.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Technology Stack ── */
function TechStackSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const stacks = [
    { category: 'Frontend', items: ['React', 'Next.js', 'Vue.js', 'TypeScript', 'Tailwind CSS'] },
    { category: 'Backend', items: ['Node.js', 'Python', 'Go', 'Java', 'GraphQL'] },
    { category: 'Mobile', items: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'iOS'] },
    { category: 'Cloud & DevOps', items: ['AWS', 'GCP', 'Azure', 'Kubernetes', 'Docker'] },
    { category: 'Data & AI', items: ['PostgreSQL', 'MongoDB', 'Redis', 'TensorFlow', 'OpenAI'] },
    { category: 'Tools', items: ['Figma', 'Jest', 'Cypress', 'GitHub Actions', 'Terraform'] },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-cpu-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Technology Stack</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Modern Stacks,
            <span className="text-primary-600"> Proven Results</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We choose the right tool for every job. Our engineers are certified and battle-tested across the full modern technology landscape.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {stacks.map((stack, index) => (
            <motion.div
              key={stack.category}
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-white rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all duration-300"
            >
              <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4">{stack.category}</h3>
              <div className="flex flex-wrap gap-2">
                {stack.items.map((item) => (
                  <span
                    key={item}
                    className="px-3 py-1.5 bg-primary-50 text-primary-700 text-xs font-semibold rounded-lg border border-primary-100"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Testimonials ── */
function TestimonialsSection() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const testimonials = [
    {
      quote: "Thoughtic shipped what our internal team estimated would take 9 months. Their React and Node.js squad integrated seamlessly with our existing stack.",
      author: "Sarah Chen",
      role: "CTO",
      company: "Fintech Startup",
      metric: "9 months → 12 weeks",
    },
    {
      quote: "The discovery phase alone saved us from building the wrong product. Their strategic input was as valuable as the code they delivered.",
      author: "Marcus Williams",
      role: "VP Product",
      company: "HealthTech Platform",
      metric: "$2M saved in rework",
    },
    {
      quote: "We needed a multi-tenant SaaS platform with real-time analytics. Thoughtic delivered with 99.95% uptime from day one. Engineering excellence.",
      author: "Priya Sharma",
      role: "Director of Engineering",
      company: "Enterprise SaaS",
      metric: "99.95% uptime SLA",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-chat-quote-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Client Voices</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            What Clients Say
            <span className="text-primary-600"> About Our Builds</span>
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, rotateX: 10 }}
              animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
              transition={{ delay: 0.25 + index * 0.12, ...springTransition, duration: 0.6 }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                ))}
              </div>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6 italic">
                "{t.quote}"
              </p>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-bold">
                  {t.author.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="text-sm font-bold text-navy-900">{t.author}</div>
                  <div className="text-xs text-gray-500">{t.role}, {t.company}</div>
                </div>
              </div>
              <div className="inline-flex items-center px-3 py-1 bg-primary-50 rounded-full">
                <span className="text-xs font-bold text-primary-600">{t.metric}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Case Study Snippet ── */
function CaseStudySnippet() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4">
              <i className="ri-briefcase-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-white/80">Client Success</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4">
              SaaS Platform Built
              <span className="text-primary-400"> in 12 Weeks</span>
            </h2>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6">
              A fintech startup needed a multi-tenant SaaS platform with real-time analytics, subscription billing, and bank-grade security. We delivered a React + Node.js product with 99.95% uptime from launch day — and helped them raise their Series A two months early.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div>
                <div className="text-2xl font-bold text-primary-400">12 Weeks</div>
                <div className="text-xs text-white/60">From kickoff to launch</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-400">99.95%</div>
                <div className="text-xs text-white/60">Launch-day uptime</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-400">$4.2M</div>
                <div className="text-xs text-white/60">Series A raised</div>
              </div>
            </div>
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Read Full Case Study
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, ...springTransition, duration: 0.7 }}
            className="relative"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20SaaS%20analytics%20dashboard%20interface%20on%20a%20desktop%20monitor%20screen%2C%20fintech%20data%20visualization%20with%20charts%20and%20graphs%2C%20clean%20dark%20UI%20with%20teal%20and%20amber%20accent%20colors%2C%20professional%20office%20environment%2C%20soft%20ambient%20lighting%2C%20product%20photography%20style%2C%20shallow%20depth%20of%20field&width=800&height=600&seq=softdev-case-1&orientation=landscape"
              alt="SaaS platform case study"
              className="rounded-2xl shadow-2xl shadow-navy-900/30 w-full"
            />
            <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white rounded-xl p-4 shadow-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
                <i className="ri-star-fill text-amber-400 text-sm"></i>
              </div>
              <p className="text-xs text-gray-600">"Thoughtic shipped what our internal team estimated would take 9 months."</p>
              <p className="text-[10px] text-gray-400 mt-0.5">— CTO, Fintech Startup</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function SoftwareDevelopmentPage() {
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as Record<string, unknown>).gtag) {
      (window as Record<string, unknown>).gtag('event', 'page_view', {
        page_title: 'Software Development Services',
        page_location: window.location.href,
        page_path: '/software-development',
        custom_parameter_1: 'software_dev_funnel',
      });
    }
  }, []);

  return (
    <PageLayout
      title="Custom Software Development Services — End-to-End Delivery"
      description="End-to-end software development: product strategy, UI/UX design, custom engineering, cloud, AI, and QA. We build web apps, mobile apps, SaaS platforms, and enterprise software."
      keywords="custom software development, web app development, SaaS platform development, mobile app development, cloud DevOps engineering, AI automation solutions, product strategy UX design, enterprise software development, QA testing services"
    >
      <Hero />
      <StatsBar />
      <WhyBuildWithUs />
      <ServicesGrid />
      <DevPricingSection />
      <DevProcess />
      <IndustriesSection />
      <TechStackSection />
      <TestimonialsSection />
      <HowWeVetEngineers
        accentColor="primary"
        heading="How We Screen Every Engineer We Deploy"
        specialtyNote="For software development projects, we additionally validate architecture decision-making, code review habits, and experience with your specific tech stack before any engineer joins your project."
      />
      <RecentProjectsCarousel />
      <CaseStudySnippet />
      <SoftwareDevFAQ />
      <LeadCaptureForm />
      <DualActionFooter />
    </PageLayout>
  );
}